import type { CosmosData, GalaxyMeta } from "../parser/types.ts";
import type { NodeDetail, NodeRef } from "../engine/types.ts";

export interface InspectorCallbacks {
  onNavigate: (id: string) => void;
  isFavorite: (id: string) => boolean;
  onToggleFavorite: (id: string) => void;
  onAgent: (id: string) => void;
}

const KIND_LABEL: Record<string, string> = {
  council: "Strategic Council",
  galaxy: "Galáxia · domínio",
  note: "Nó",
};

/** Painel de inspeção: transforma o Cosmos em navegador de conhecimento.
 *  Mostra metadados, hierarquia e conexões; tudo clicável; deep-link ao Obsidian. */
export class Inspector {
  private el!: HTMLElement;
  private body!: HTMLElement;
  private current: NodeDetail | null = null;

  constructor(private data: CosmosData, private cb: InspectorCallbacks) {}
  setData(d: CosmosData) { this.data = d; }

  mount(root: HTMLElement) {
    root.insertAdjacentHTML(
      "beforeend",
      `<aside id="inspector" class="panel"><button id="insp-close" aria-label="fechar">×</button><div id="insp-body"></div></aside>`,
    );
    this.el = root.querySelector("#inspector") as HTMLElement;
    this.body = root.querySelector("#insp-body") as HTMLElement;
    (root.querySelector("#insp-close") as HTMLElement).addEventListener("click", () => this.hide());
    this.body.addEventListener("click", (e) => {
      const fav = (e.target as HTMLElement).closest("[data-fav]") as HTMLElement | null;
      if (fav?.dataset.fav) { this.cb.onToggleFavorite(fav.dataset.fav); this.refresh(); return; }
      const ag = (e.target as HTMLElement).closest("[data-agent]") as HTMLElement | null;
      if (ag?.dataset.agent) { this.cb.onAgent(ag.dataset.agent); return; }
      const t = (e.target as HTMLElement).closest("[data-nav]") as HTMLElement | null;
      if (t?.dataset.nav) this.cb.onNavigate(t.dataset.nav);
    });
  }

  hide() { this.el?.classList.remove("open"); document.body.classList.remove("insp-open"); }
  refresh() { if (this.current) { this.body.innerHTML = this.render(this.current); if (this.current.id.startsWith("eco:")) this.loadSparks(this.current.id); } }
  show(d: NodeDetail | null) {
    this.current = d;
    if (!d) { this.hide(); return; }
    this.body.innerHTML = this.render(d);
    this.el.classList.add("open");
    document.body.classList.add("insp-open");
    this.body.scrollTop = 0;
    if (d.id.startsWith("eco:")) this.loadSparks(d.id);
  }

  private galColor(g: string): string {
    return (this.data.gal as Record<string, GalaxyMeta>)[g]?.color ?? "#8893ad";
  }
  private chip(r: NodeRef): string {
    return `<button class="navchip" data-nav="${esc(r.id)}"><span class="d" style="background:${this.galColor(r.gal)}"></span>${esc(r.label)}</button>`;
  }

  private renderVitals(m: Record<string, string | number>, at?: string): string {
    const F: Record<string, (v: string | number) => string> = {
      up: (v) => (Number(v) ? "✓ no ar" : "✗ fora"),
      http: (v) => `HTTP ${esc(String(v))}`,
      ms: (v) => `${v} ms`,
      engaj: (v) => `${Math.round(Number(v) * 100)}%`,
      gasto_brl: (v) => `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      custo_lead: (v) => `R$ ${Number(v).toFixed(2)}`,
      cap_pct: (v) => `${Number(v).toFixed(0)}%`,
      receita_usd: (v) => `US$ ${Number(v).toFixed(2)}`,
      rpm: (v) => `US$ ${Number(v).toFixed(2)}`,
    };
    const LBL: Record<string, string> = {
      up: "status", http: "resposta", ms: "latência", usuarios: "usuários 28d", sessoes: "sessões 28d",
      engaj: "engajamento", gasto_brl: "gasto", conversas: "conversas", custo_lead: "custo/lead",
      campanhas: "campanhas", cap_pct: "orçamento", receita_usd: "receita 30d", rpm: "RPM", impr_gam: "impressões 30d",
    };
    const order = ["up", "http", "ms", "receita_usd", "rpm", "impr_gam", "usuarios", "sessoes", "engaj",
      "gasto_brl", "conversas", "custo_lead", "campanhas", "cap_pct"];
    const rank = (k: string) => (order.indexOf(k) + 1 || 99);
    const noSpark = new Set(["up", "http"]);
    const cells = Object.keys(m).sort((a, b) => rank(a) - rank(b)).map((k) => {
      const val = F[k] ? F[k](m[k]) : esc(String(m[k]));
      const cls = k === "up" ? (Number(m[k]) ? " vital-ok" : " vital-down") : "";
      const spark = noSpark.has(k) ? "" : `<div class="vital-spark" data-spark="${esc(k)}"></div>`;
      return `<div class="vital${cls}"><div class="vital-val">${val}</div><div class="vital-lbl">${esc(LBL[k] || k)}</div>${spark}</div>`;
    }).join("");
    const ago = at ? `<div class="vital-at">medido ${esc(new Date(at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }))}</div>` : "";
    return `<div class="insp-sec"><h3>Sinais vitais</h3><div class="vitals">${cells}</div>${ago}</div>`;
  }

  /** desenha sparklines (SVG inline) a partir do histórico de métricas do nó */
  private async loadSparks(id: string) {
    try {
      const r = await fetch(`/api/metrics/history?id=${encodeURIComponent(id)}&t=${Date.now()}`, { cache: "no-store" });
      if (!r.ok) return;
      const { series } = await r.json();
      if (!series || this.current?.id !== id) return;
      for (const el of Array.from(this.body.querySelectorAll<HTMLElement>("[data-spark]"))) {
        const pts = series[el.dataset.spark as string];
        if (pts && pts.length >= 2) el.innerHTML = sparkSvg(pts);
      }
    } catch { /* sem histórico ainda */ }
  }

  private render(d: NodeDetail): string {
    const obs = d.path
      ? `obsidian://open?vault=${encodeURIComponent(this.data.vault || "")}&file=${encodeURIComponent(d.path.replace(/\.md$/, ""))}`
      : null;
    const metaRows = Object.entries(d.meta)
      .filter(([k]) => k !== "peso")
      .map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`)
      .join("");
    const fav = this.cb.isFavorite(d.id);
    const node = (this.data.nodes as Array<{ id: string; metrics?: Record<string, string | number>; metricsAt?: string }>).find((n) => n.id === d.id);
    const vitals = node?.metrics && Object.keys(node.metrics).length ? this.renderVitals(node.metrics, node.metricsAt) : "";
    return `
      <div class="insp-head">
        <div class="insp-gal"><span class="d" style="background:${d.galColor}"></span>${esc(d.galLabel)}
          <button class="insp-star${fav ? " on" : ""}" data-fav="${esc(d.id)}" title="${fav ? "remover dos favoritos" : "favoritar (F)"}">${fav ? "★" : "☆"}</button>
        </div>
        <h2>${esc(d.label)}</h2>
        <div class="insp-sub">${KIND_LABEL[d.kind] ?? "Nó"}${d.peso ? ` · peso ${d.peso}` : ""} · ${d.childCount} filhos · ${d.linkCount} conexões</div>
        ${obs ? `<a class="insp-obsidian" href="${obs}">⤓ Abrir no Obsidian</a>` : ""}
      </div>
      ${vitals}
      ${d.metrics.length ? `<div class="insp-sec"><h3>Dashboard</h3><div class="kpi-grid">${d.metrics.map((m) => `<div class="kpi"><div class="kpi-val">${esc(m.value)}</div><div class="kpi-lbl">${esc(m.label)}</div></div>`).join("")}</div></div>` : ""}
      ${d.kind !== "note" ? `<button class="insp-agent" data-agent="${esc(d.id)}">💬 Falar com o agente desta galáxia</button>` : ""}
      ${metaRows ? `<div class="insp-sec"><h3>Propriedades</h3><table class="insp-meta">${metaRows}</table></div>` : ""}
      ${d.parent ? `<div class="insp-sec"><h3>Pertence a</h3><div class="navlist">${this.chip(d.parent)}</div></div>` : ""}
      ${d.children.length ? `<div class="insp-sec"><h3>Contém · ${d.children.length}</h3><div class="navlist">${d.children.map((c) => this.chip(c)).join("")}</div></div>` : ""}
      ${d.connections.length ? `<div class="insp-sec"><h3>Conexões · ${d.connections.length}</h3><div class="navlist">${d.connections.map((c) => this.chip(c)).join("")}</div></div>` : ""}
    `;
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}

/** sparkline SVG inline a partir de uma série [{t,v}] (tendência: verde sobe, vermelho desce) */
function sparkSvg(pts: Array<{ t: number; v: number }>): string {
  const W = 92, H = 22, pad = 2.5;
  const vs = pts.map((p) => p.v);
  const min = Math.min(...vs), max = Math.max(...vs), span = (max - min) || 1;
  const x = (i: number) => pad + (i / (pts.length - 1)) * (W - 2 * pad);
  const y = (v: number) => pad + (1 - (v - min) / span) * (H - 2 * pad);
  const path = pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  const trend = last.v - pts[0].v;
  const col = trend > 0 ? "#34d399" : trend < 0 ? "#f87171" : "#7dd3fc";
  return `<svg class="spark-svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" preserveAspectRatio="none" style="color:${col}">`
    + `<path d="${path}" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>`
    + `<circle cx="${x(pts.length - 1).toFixed(1)}" cy="${y(last.v).toFixed(1)}" r="1.7" fill="currentColor"/></svg>`;
}
